/**
 * Carrello a tendina.
 *
 * Intercetta l'invio dei moduli verso /cart/add, invia con fetch e chiede a
 * Shopify di ri-renderizzare la sezione con la Section Rendering API.
 *
 * Il markup lo produce sempre Liquid, mai il JavaScript: ricostruire le righe
 * del carrello lato client significherebbe mantenere due volte la stessa
 * vista, e prima o poi le due divergono.
 *
 * Senza questo script i moduli inviano normalmente e si atterra sulla pagina
 * carrello: la vendita non dipende dallo script.
 */
class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('[data-cart-panel]');
    this.overlay = this.querySelector('[data-cart-overlay]');
    this.errorBox = this.querySelector('[data-cart-error]');
    if (!this.panel) return;

    this.lastTrigger = null;

    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    this.querySelector('[data-cart-close]')?.addEventListener('click', this.close);
    this.overlay?.addEventListener('click', this.close);
    this.addEventListener('keydown', this.onKeydown);

    this.bindGlobal();
    this.bindInside();
  }

  /** Intercetta ogni modulo di aggiunta al carrello presente in pagina. */
  bindGlobal() {
    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.action || !form.action.includes('/cart/add')) return;

      event.preventDefault();
      this.addToCart(form);
    });

    document.querySelectorAll('[data-cart-toggle]').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.lastTrigger = btn;
        this.open();
      });
    });
  }

  /** Quantita' e rimozione dentro la tendina, ri-agganciate a ogni render. */
  bindInside() {
    this.panel.querySelectorAll('[data-line-change]').forEach((el) => {
      el.addEventListener('click', (event) => {
        event.preventDefault();
        this.changeLine(el.dataset.line, el.dataset.lineChange);
      });
    });
  }

  async addToCart(form) {
    const submit = form.querySelector('[type="submit"]');
    const body = new FormData(form);
    // Chiede a Shopify di restituire anche la sezione gia' renderizzata.
    body.append('sections', 'cart-drawer');

    this.showError('');
    if (submit) submit.disabled = true;

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body,
      });
      const data = await response.json();

      if (!response.ok) {
        // Lo slot puo' essersi esaurito fra la scelta e l'invio: nascondere
        // l'errore lascerebbe la persona a credere di aver comprato.
        this.showError(data.description || data.message || '');
        this.lastTrigger = submit;
        this.open();
        return;
      }

      this.render(data.sections?.['cart-drawer']);
      this.lastTrigger = submit;
      this.open();
    } catch {
      // Rete assente: si lascia proseguire il modulo in modo tradizionale.
      form.submit();
    } finally {
      if (submit) submit.disabled = false;
    }
  }

  async changeLine(line, quantity) {
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(line), quantity: Number(quantity), sections: 'cart-drawer' }),
      });
      const data = await response.json();
      if (response.ok) this.render(data.sections?.['cart-drawer']);
    } catch {
      window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
    }
  }

  /** Sostituisce il contenuto con l'HTML renderizzato da Liquid. */
  render(html) {
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('[data-cart-panel]');
    if (!fresh) return;

    this.panel.innerHTML = fresh.innerHTML;
    this.errorBox = this.querySelector('[data-cart-error]');
    this.querySelector('[data-cart-close]')?.addEventListener('click', this.close);
    this.bindInside();

    const count = doc.querySelector('[data-cart-count]')?.textContent?.trim();
    if (count !== undefined) {
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = count;
        el.hidden = count === '0';
      });
    }
  }

  showError(message) {
    if (!this.errorBox) return;
    this.errorBox.textContent = message;
    this.errorBox.hidden = !message;
  }

  open() {
    this.classList.add('is-open');
    if (this.overlay) this.overlay.hidden = false;
    document.documentElement.classList.add('drawer-open');

    // Vedi navigation-drawer.js: il focus va spostato dopo che il browser ha
    // applicato lo stile, altrimenti cade su un elemento ancora invisibile.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.focusables()[0]?.focus());
    });
  }

  close() {
    this.classList.remove('is-open');
    if (this.overlay) this.overlay.hidden = true;
    document.documentElement.classList.remove('drawer-open');
    // Il focus torna a chi ha aperto: senza, chi naviga da tastiera
    // ripartirebbe dall'inizio del documento.
    this.lastTrigger?.focus();
  }

  get isOpen() {
    return this.classList.contains('is-open');
  }

  focusables() {
    return Array.from(
      this.panel.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => el.offsetParent !== null);
  }

  onKeydown(event) {
    if (!this.isOpen) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key !== 'Tab') return;

    const items = this.focusables();
    if (items.length === 0) return;

    const first = items[0];
    const last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}
